/**
 * Field Job Order Notification Mixin
 *
 * Mount on App.vue to enable:
 * - Popup on login showing field job orders created/updated/cancelled
 *   since yesterday (requires the `dashboard.field_job_orders` permission)
 * - Continuous polling every 2 minutes for new/updated/cancelled orders
 */

var NOTIFICATION_POLL_INTERVAL = 120000; // 2 minutes

export default {
  data() {
    return {
      _fjoNotificationPollInterval: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initFieldJobOrderNotifications();
    });
  },
  beforeDestroy() {
    this.stopFieldJobOrderInterval();
  },
  methods: {
    async initFieldJobOrderNotifications() {
      var user = this.$store.getters["auth/user"];
      if (!user) return;

      // Clean up any existing interval before starting a new one
      this.stopFieldJobOrderInterval();

      // Only users with the field job orders permission get notifications
      await this.$store.dispatch("permission/ensure_user_permissions");
      if (
        !this.$store.getters["permission/hasPermission"](
          "dashboard.field_job_orders"
        )
      )
        return;

      // On login, look back 24 hours so the first fetch catches orders
      // logged since yesterday (the "notify me on login" behavior)
      if (!this.$store.getters["field_job_order/last_notification_check"]) {
        this.$store.commit(
          "field_job_order/SET_LAST_NOTIFICATION_CHECK",
          new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        );
      }

      // Fetch immediately on login
      this.$store
        .dispatch("field_job_order/fetch_fjo_notifications")
        .catch((e) => {
          console.warn(
            "[FieldJobOrderNotification] Initial fetch failed:",
            e.message || e
          );
        });

      // Start polling every 2 minutes
      this._fjoNotificationPollInterval = setInterval(() => {
        this.pollFieldJobOrderNotifications();
      }, NOTIFICATION_POLL_INTERVAL);
    },

    /**
     * Stop the polling interval. Called on beforeDestroy and before re-init.
     */
    stopFieldJobOrderInterval() {
      if (this._fjoNotificationPollInterval) {
        clearInterval(this._fjoNotificationPollInterval);
        this._fjoNotificationPollInterval = null;
      }
    },

    /**
     * Clear notification state and stop the interval. Called on logout.
     */
    cleanupFieldJobOrderNotifications() {
      this.stopFieldJobOrderInterval();
      this.$store.dispatch("field_job_order/clear_notification_state");
    },

    /**
     * Poll the server for new/updated/cancelled field job orders.
     * Called every 2 minutes. Errors are caught silently to prevent
     * notification spam on network issues.
     */
    pollFieldJobOrderNotifications() {
      var user = this.$store.getters["auth/user"];
      if (!user) return;

      // Do not poll if the notification dialog is already open
      // to avoid overwriting the currently displayed notifications
      if (this.$store.getters["field_job_order/show_notification_dialog"])
        return;

      this.$store
        .dispatch("field_job_order/fetch_fjo_notifications")
        .catch((e) => {
          // Silently ignore network errors -- the next poll will retry
          console.warn(
            "[FieldJobOrderNotification] Poll failed:",
            e.message || e
          );
        });
    },
  },
};
